import { useEffect, useRef, useCallback, useState } from "react";
import { useAuth } from "../context/AuthProvider";

const SESSION_TTL_SECONDS    = Number(import.meta.env.VITE_SESSION_TTL_SECONDS    || 32400);
const WARNING_BEFORE_SECONDS = Number(import.meta.env.VITE_WARNING_BEFORE_SECONDS || 30);
const COUNTDOWN_SECONDS      = Number(import.meta.env.VITE_COUNTDOWN_SECONDS      || 10);

// Warn popup appears this many ms before session expires
const WARN_AFTER_MS = (SESSION_TTL_SECONDS - WARNING_BEFORE_SECONDS) * 1000;

interface UseSessionGuardReturn {
  showWarning: boolean;
  countdown: number;
  keepAlive: () => void;
}

export function useSessionGuard(): UseSessionGuardReturn {
  const { user, logout } = useAuth();

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown]     = useState(COUNTDOWN_SECONDS);

  const warnTimerRef      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const sseRef            = useRef<EventSource | null>(null);
  const isExtendingRef    = useRef(false);

  // ── Clear all timers ────────────────────────────────────────────────────────
  const clearAllTimers = useCallback(() => {
    if (warnTimerRef.current)    clearTimeout(warnTimerRef.current);
    if (countdownRef.current)    clearInterval(countdownRef.current);
    warnTimerRef.current   = null;
    countdownRef.current   = null;
  }, []);

  // ── Start countdown → auto logout after COUNTDOWN_SECONDS ──────────────────
  const startCountdown = useCallback(() => {
    setCountdown(COUNTDOWN_SECONDS);
    setShowWarning(true);

    let remaining = COUNTDOWN_SECONDS;

    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);

      if (remaining <= 0) {
        clearInterval(countdownRef.current!);
        countdownRef.current = null;
        setShowWarning(false);
        logout();
      }
    }, 1000);
  }, [logout]);

  // ── Schedule the warning popup ──────────────────────────────────────────────
  const scheduleWarning = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);

    if (WARN_AFTER_MS <= 0) {
      // TTL too short to show warning — just logout directly
      warnTimerRef.current = setTimeout(() => logout(), SESSION_TTL_SECONDS * 1000);
      return;
    }

    warnTimerRef.current = setTimeout(() => {
      startCountdown();
    }, WARN_AFTER_MS);
  }, [clearAllTimers, startCountdown, logout]);

  // ── Keep Alive — user clicked "Stay Logged In" ──────────────────────────────
  // Calls backend to refresh Redis TTL, then reschedules warning timer
  const keepAlive = useCallback(async () => {
    if (isExtendingRef.current) return;
    isExtendingRef.current = true;

    try {
      const res = await fetch(
        `${import.meta.env.VITE_APP_SERVER}/api/auth/keep-alive`,
        { method: "POST", credentials: "include" },
      );

      if (res.ok) {
        clearAllTimers();
        setShowWarning(false);
        setCountdown(COUNTDOWN_SECONDS);
        scheduleWarning(); // restart the warning timer from scratch
      } else {
        // Session already invalid on backend — logout
        logout();
      }
    } catch {
      logout();
    } finally {
      isExtendingRef.current = false;
    }
  }, [clearAllTimers, scheduleWarning, logout]);

  // ── SSE — force-logout from another device ──────────────────────────────────
  const connectSSE = useCallback(() => {
    if (!user) return;

    if (sseRef.current) {
      sseRef.current.close();
    }

    const es = new EventSource(
      `${import.meta.env.VITE_APP_SERVER}/api/auth/session-events`,
      { withCredentials: true },
    );

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.event === "force-logout") {
          es.close();
          clearAllTimers();
          window.dispatchEvent(
            new CustomEvent("force-logout", { detail: data.message }),
          );
        }
      } catch {}
    };

    es.onerror = () => {
      // EventSource auto-reconnects — no action needed
    };

    sseRef.current = es;
  }, [user, clearAllTimers]);

  // ── Mount: connect SSE + schedule warning ───────────────────────────────────
  useEffect(() => {
    if (!user) return;

    connectSSE();
    scheduleWarning();

    return () => {
      clearAllTimers();
      sseRef.current?.close();
    };
  }, [user]); 

  return { showWarning, countdown, keepAlive };
}